import { getField, getTicketRates } from "@simplyk/common";

import { useTranslate } from "../../../../../common/hooks/useTranslate";
import { TicketingRecurrenceData } from "../../Steps/StepDate/types";
import { useLiveFormEditorContext } from "../LiveFormEditorContext";
import { getPlaceholderOccurrenceWithRates, getPlaceholderRates } from "../prefillDataBuilders/prefillRates";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import {
  RateOutput,
  OccurrenceWithRatesOutput,
  TicketingRecurrenceOutput,
  TicketingPreviewOutput,
} from "@/common/types/trpc";
import { getFormCategoryInfo } from "@/features/LiveFormEditor/common/formCategory.helper";
import { defaultLiveFormEditorPreviewPlaceholders } from "@/features/LiveFormEditor/LivePreview/contexts/PreviewContext";
import { DEFAULT_AUTOMATED_EMAIL_OPENED_CARD } from "@/features/LiveFormEditor/Steps/StepAutomatedCommunications/types";
import { LiveTicketingData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";

export const useBuildInitialLiveTicketingData = () => {
  const { t } = useTranslate();
  const { organization: currentOrganization } = useCurrentUserContext();
  const { stepProgress, getIndexStepFromKey } = useLiveFormEditorContext();
  const furthestStepReached = stepProgress.reached;

  const buildInitialLiveTicketingData = (apiTicketing: TicketingPreviewOutput): LiveTicketingData => {
    const { formCategory } = apiTicketing;
    const { isAuction, isEvent, isLottery } = getFormCategoryInfo(apiTicketing.formCategory);

    const rates =
      furthestStepReached < getIndexStepFromKey("tickets")
        ? getPlaceholderRates({ formCategory, t, locale: apiTicketing.locale })
        : getTicketRates(apiTicketing.rates);

    const supportsOccurrences = isEvent || isLottery || isAuction;
    let occurrencesWithRates: OccurrenceWithRatesOutput[] = [];
    if (supportsOccurrences) {
      // If the user has not reached the Date step yet, use a placeholder
      occurrencesWithRates =
        furthestStepReached < getIndexStepFromKey("date")
          ? [getPlaceholderOccurrenceWithRates({ formCategory, t, locale: apiTicketing.locale })]
          : mapRecurrencesToOccurrencesWithRates(apiTicketing.recurrences || [], rates, apiTicketing.id);
    }

    const supportAddress = isEvent || isAuction;
    let address: string | null = null;
    if (supportAddress) {
      // If the user has not reached the Location step yet, use placeholder
      address =
        furthestStepReached < getIndexStepFromKey("location")
          ? t("dashboard", "liveFormEditor.address.placeholder")
          : apiTicketing.address;
    }

    const organization = {
      ...apiTicketing.organization,
      name: currentOrganization?.name || "",
    };

    const defaultLocaleField = getField(apiTicketing.ticketingFields, apiTicketing.locale);
    const ticketingFields = defaultLocaleField ? [defaultLocaleField] : apiTicketing.ticketingFields;

    return {
      ticketing: { ...apiTicketing, address, rates, organization, ticketingFields },
      occurrencesWithRates,
      selectedOccurrenceWithRates: occurrencesWithRates.length === 1 ? occurrencesWithRates[0] : null,
      placeholders: {
        ...defaultLiveFormEditorPreviewPlaceholders,
        isDate: furthestStepReached < getIndexStepFromKey("date"),
        isLocation: furthestStepReached < getIndexStepFromKey("location"),
        isLogo: furthestStepReached < getIndexStepFromKey("logo"),
        isDescription: furthestStepReached < getIndexStepFromKey("description"),
        isExtraDonation:
          furthestStepReached < Math.max(getIndexStepFromKey("add-donation"), getIndexStepFromKey("add-ons")) ||
          !apiTicketing.extraDonation,
        isGuestQuestions: furthestStepReached < getIndexStepFromKey("questions"),
        isDiscountCode: furthestStepReached < getIndexStepFromKey("discount-codes"),
        isBuyerQuestions: furthestStepReached < getIndexStepFromKey("questions"),
        isFundraisingGoal: furthestStepReached < getIndexStepFromKey("fundraising-goal"),
      },
      automatedCommunicationsData: {
        emailInPreview: DEFAULT_AUTOMATED_EMAIL_OPENED_CARD,
        thankYou: null,
        thankYouBidPlaced: null,
        thankYouNonWinner: null,
        reminder1: null,
        reminder2: null,
        expiringMembership: null,
        autoRenewal: null,
        readyForPickup: null,
        orderShipped: null,
      },
      eTicketPreviewInFocus: "e-ticket",
    };
  };

  return { buildInitialLiveTicketingData };
};

export const mapRecurrencesToOccurrencesWithRates = (
  recurrences: TicketingRecurrenceOutput[] | TicketingRecurrenceData[],
  rates: RateOutput[] | RateOutput[],
  ticketingId: string
): OccurrenceWithRatesOutput[] => {
  return recurrences.map((recurrence) => ({
    occurrence: {
      id: recurrence.id,
      ticketingId,
      startUtc: getValidDateISOString(recurrence?.startUtc),
      endUtc: getValidDateISOString(recurrence?.endUtc),
      isArchived: false,
      closed: false,
      closeDate: null,
      openDate: parseOptionalDateToIsoString(recurrence?.openDate),
    },
    rates: rates.map((r) => ({
      rateId: r.id,
      remainingTickets: (r.seats ?? 1) * (r.attendeesByTicket ?? 1),
      soldTicketsCount: 0,
    })),
  }));
};

const getValidDateISOString = (dateValue: Date | string | null | undefined): string => {
  if (!dateValue) {
    return new Date().toISOString();
  }

  const date = new Date(dateValue);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return new Date().toISOString();
  }

  return date.toISOString();
};

const parseOptionalDateToIsoString = (dateValue: Date | string | null | undefined): string | null => {
  if (!dateValue) {
    return null;
  }
  const date = new Date(dateValue);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
};
